import React, { useState, useMemo, useContext } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import Bread from "../../common/Bread";
import { Blurhash } from "react-blurhash";
import { motion } from "framer-motion";
import "./teams.scss";
import Loader from "../../common/Loader";
import ItTeams from "./ItTeams";
import { LangContext } from "../../context/LangContext";
import { getImageAlt } from "../../utils/imageAlt";

const Teams = () => {
  const [title, setTitle] = useState("");
  const [imageLoading, setLoading] = useState(true);
  const { slug } = useParams();

  const lang = useContext(LangContext);
  const nep = lang.state.np;

  function titleSwitch() {
    switch (slug) {
      case "bod":
        setTitle(nep ? "सञ्चालक समिति" : "Board Of Directors");
        break;
      case "management-team":
        setTitle(nep ? "व्यवस्थापन समूह" : "Management Team");
        break;
      case "hod":
        setTitle(nep ? "विभागीय प्रमुख" : "Head Of Department");
        break;
      case "regional-head":
        setTitle(nep ? "प्रदेश प्रमुख" : "Regional Head");
        break;
      case "company-secretary":
        setTitle(nep ? "कम्पनी सचिव" : "Company Secretary");
        break;
      default:
        setTitle(nep ? "समूह" : "Teams");
        break;
    }
  }

  useMemo(titleSwitch, [slug, nep]);

  const onLoaded = () => {
    setLoading(false);
  };

  const { isLoading, error, data, isFetching } = useQuery({
    queryKey: ["teamData", slug],
    queryFn: () =>
      axios
        .get(`${process.env.REACT_APP_BASE_URL}/team/all/${slug}`)
        .then((res) => res.data),
  });

  if (isFetching) return <Loader />;
  if (isLoading) return <Loader />;
  if (error) return "An error has occurred: " + error.message;

  const items = nep ? data.items.np : data.items.en;

  const breadcrumList = {
    image: "",
    title: title,
    l1: nep ? "होम" : "Home",
    l2: nep ? "समूह" : "Teams",
    l3: "",
  };

  if (slug === "it-officers") return <ItTeams data={data} />;

  const blockTrue = items.find((obj) => obj.is_block === "Yes");

  const otherMembers = items.filter((obj) => obj.is_block !== "Yes");

  return (
    <Bread data={breadcrumList}>
      {blockTrue && (
        <div className="teams-block" tabIndex={0} role="region" aria-label={blockTrue.name}>
          <div className="teams-block-profile-container">
            <div className="blurhash-wrapper">
              <Blurhash
                hash={blockTrue.blur}
                width={250}
                height={340}
                resolutionX={32}
                resolutionY={32}
                punch={1}
              />

              <motion.img
                initial={{ opacity: 0 }}
                animate={{ opacity: imageLoading ? 0 : 1 }}
                transition={{ opacity: { delay: 0.5, duration: 0.4 } }}
                loading="lazy"
                onLoad={onLoaded}
                className="image"
                src={blockTrue.featured_image}
                alt={getImageAlt(blockTrue, blockTrue.designation_name)}
                width={250}
                height={340}
              />
            </div>
          </div>

          <div className="chairmain-details">
            <h3 tabIndex={0}>{blockTrue.name}</h3>
            <h4 tabIndex={0}>{blockTrue.designation_name}</h4>
          </div>
        </div>
      )}

      <div className="teamsPageMainContainer" tabIndex={0} role="region" aria-label={nep ? "टोली सदस्यहरू" : "Team Members"}>
        {otherMembers.map((i, index) => (
          <div key={index} className="teams-profile-container" tabIndex={0} role="region" aria-label={i.name}>
            <div className="team-image-box">
              <div className="blurhash-wrapper">
                <Blurhash
                  hash={i.blur}
                  width={240}
                  height={330}
                  resolutionX={32}
                  resolutionY={32}
                  punch={1}
                />

                <motion.img
                  initial={{ opacity: 0 }}
                  animate={{ opacity: imageLoading ? 0 : 1 }}
                  transition={{ opacity: { delay: 0.5, duration: 0.4 } }}
                  loading="lazy"
                  onLoad={onLoaded}
                  className="image"
                  src={i.featured_image}
                  alt={getImageAlt(i, i.designation_name)}
                  width={240}
                  height={330}
                />
              </div>
            </div>

            <h3 tabIndex={0}>{i.name}</h3>

            {slug === "hod" ? (
              <h4 tabIndex={0}>{i.department_name}</h4>
            ) : (
              <h4 tabIndex={0}>{i.designation_name}</h4>
            )}

            {slug === "regional-head" && <h4 tabIndex={0}>{i.sub_designation}</h4>}
            {slug === "company-secretary" && <h4 tabIndex={0}>{i.contact}</h4>}
            {slug === "company-secretary" && <h4 tabIndex={0}>{i.email}</h4>}
          </div>
        ))}
      </div>
    </Bread>
  );
};

export default Teams;
